// Thème commun des graphiques statistiques (chart.js / vue-chartjs).
// Couleurs validées pour la lisibilité (daltonisme, contraste sur fond blanc).

export const SERIES_RESERVATION = "#2a4fa8";
export const SERIES_LUGGAGE = "#159e72";

const AXIS_TEXT = "#475569";
const GRID_LINE = "#eef2f6";
const FONT = { family: "Montserrat, sans-serif", size: 12 };

// Au-delà, les éléments d'un classement sont regroupés dans « Autres »
export const RANKING_LIMIT = 8;

export const formatNumber = (value) =>
  new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(Number(value) || 0);

// Une valeur de série peut arriver sous forme [clé, valeur] (Object.entries) : on garde le nombre
const toNumber = (value) => Number(Array.isArray(value) ? value[1] : value) || 0;

export const barDataset = (label, data, color) => ({
  label,
  data,
  backgroundColor: color,
  hoverBackgroundColor: color,
  borderRadius: 4,
  borderSkipped: "start",
  maxBarThickness: 28,
  categoryPercentage: 0.7,
  barPercentage: 0.9,
});

// Barres groupées alignées sur les libellés de la première série.
// series : [{ label, map, color }] — une série dont aucune clé ne correspond aux libellés est ignorée.
export const buildGroupedBars = (series) => {
  const [first] = series;
  const labels = Object.keys((first && first.map) || {});
  const datasets = series
    .filter((s, index) => index === 0 || labels.some((l) => s.map && l in s.map))
    .map((s) => barDataset(s.label, labels.map((l) => toNumber((s.map || {})[l])), s.color));
  return { labels, datasets };
};

// Classement décroissant, les suivants regroupés dans « Autres »
export const buildRanking = (map, label, color = SERIES_LUGGAGE) => {
  const entries = Object.entries(map || {})
    .map(([key, value]) => [key, toNumber(value)])
    .sort((a, b) => b[1] - a[1]);
  if (entries.length > RANKING_LIMIT) {
    const others = entries.slice(RANKING_LIMIT - 1).reduce((sum, [, v]) => sum + v, 0);
    entries.splice(RANKING_LIMIT - 1, entries.length, ["Autres", others]);
  }
  return {
    labels: entries.map(([key]) => key),
    datasets: [barDataset(label, entries.map(([, v]) => v), color)],
  };
};

// Reconstitue un objet { libellé: valeur } à partir d'un chartData existant
export const mapFromChartData = (chartData, datasetIndex = 0) => {
  const labels = (chartData && chartData.labels) || [];
  const dataset = chartData && chartData.datasets && chartData.datasets[datasetIndex];
  const data = (dataset && dataset.data) || [];
  return labels.reduce((map, label, i) => ({ ...map, [label]: toNumber(data[i]) }), {});
};

export const hasData = (chart) =>
  chart.labels.length > 0 && chart.datasets.some((d) => d.data.some((v) => v > 0));

export const rankingHeight = (chart) => ({
  height: Math.max(160, chart.labels.length * 40 + 40) + "px",
  position: "relative",
});

export const CHART_STYLES = { height: "300px", position: "relative" };

// Options communes : grille horizontale légère, axes discrets, infobulle formatée
export const chartOptions = ({ currency = false, legend = true, horizontal = false } = {}) => {
  const fmt = (v) => formatNumber(v) + (currency ? " FCFA" : "");
  const valueAxis = {
    beginAtZero: true,
    grid: { color: GRID_LINE, drawBorder: false },
    ticks: { color: AXIS_TEXT, font: FONT, maxTicksLimit: 5, callback: (v) => formatNumber(v) },
  };
  const categoryAxis = {
    grid: { display: false, drawBorder: false },
    ticks: { color: AXIS_TEXT, font: FONT, autoSkip: true, maxRotation: 0 },
  };
  return {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: horizontal ? "y" : "x",
    interaction: { mode: "index", intersect: false },
    scales: horizontal ? { x: valueAxis, y: categoryAxis } : { x: categoryAxis, y: valueAxis },
    plugins: {
      legend: {
        display: legend,
        position: "top",
        align: "end",
        labels: { usePointStyle: true, pointStyle: "rectRounded", boxWidth: 10, color: AXIS_TEXT, font: FONT },
      },
      tooltip: {
        backgroundColor: "#0f172a",
        titleFont: { ...FONT, weight: "700" },
        bodyFont: FONT,
        padding: 10,
        cornerRadius: 8,
        boxPadding: 4,
        callbacks: { label: (ctx) => ` ${ctx.dataset.label} : ${fmt(ctx.parsed[horizontal ? "x" : "y"])}` },
      },
    },
  };
};
